import React, { useState, useEffect, forwardRef } from 'react';
import { Input, Tabs, Form } from 'antd';
import styled from '@emotion/styled';
import { useLocaleStore } from '../../stores/localeStore';
import { LocalisedField, ILanguage } from '../../interfaces';

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
    
    .ant-tabs-tab {
      background: #f5f5f5;
      border: 1px solid #d9d9d9;
      border-radius: 6px 6px 0 0;
      margin-right: 4px;
      padding: 6px 12px;
      transition: all 0.2s ease;
      
      &:hover {
        background: #e6f7ff;
        border-color: #91d5ff;
        color: #1890ff;
      }
      
      &.ant-tabs-tab-active {
        background: #195178;
        border-color: #195178;
        color: white;
        font-weight: 500;
        
        .ant-tabs-tab-btn {
          color: white;
        }
      }
    }
    
    .ant-tabs-ink-bar {
      display: none;
    }
  }
  
  .ant-tabs-content-holder {
    border: 1px solid #d9d9d9;
    border-radius: 0 6px 6px 6px;
    background: white;
  }
  
  .ant-tabs-tabpane {
    padding: 0;
  }
`;

interface LocalisedFieldInputProps {
  value?: LocalisedField[];
  onChange?: (value: LocalisedField[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  name?: string;
  rows?: number; // For TextArea
  type?: 'input' | 'textarea';
  // Additional props for better Ant Design integration
  status?: 'error' | 'warning';
  id?: string;
}

const LocalisedFieldInput: React.FC<LocalisedFieldInputProps> = ({
  value = [],
  onChange,
  placeholder = '',
  disabled = false,
  required = false,
  label,
  name,
  rows = 3,
  type = 'input',
  status,
  id
}) => {
  const { languages, activeLanguage } = useLocaleStore();
  const [activeTab, setActiveTab] = useState<string>('');
  const [localValues, setLocalValues] = useState<LocalisedField[]>([]);

  // Initialize activeTab only once when languages are first loaded
  useEffect(() => {
    if (languages.length > 0 && !activeTab) {
      // Set default active tab to the first language or current active language
      const defaultLanguage = activeLanguage || languages[0];
      setActiveTab(defaultLanguage.id.toString());
    }
  }, [languages, activeLanguage]);

  // Initialize and update localValues based on value prop
  useEffect(() => {
    if (languages.length > 0) {
      // Calculate what the new values should be
      const newValues = value.length === 0
        ? languages.map(lang => ({
            languageId: lang.id,
            value: ''
          }))
        : languages.map(lang => {
            const existingValue = value.find(v => v.languageId === lang.id);
            return existingValue || { languageId: lang.id, value: '' };
          });
      
      // Only update if values have actually changed (deep comparison)
      // Compare current localValues with newValues to prevent infinite loops
      setLocalValues(prevValues => {
        const prevValuesSorted = [...prevValues].sort((a, b) => a.languageId - b.languageId);
        const newValuesSorted = [...newValues].sort((a, b) => a.languageId - b.languageId);
        const prevValuesStr = JSON.stringify(prevValuesSorted);
        const newValuesStr = JSON.stringify(newValuesSorted);
        if (prevValuesStr !== newValuesStr) {
          return newValues;
        }
        return prevValues;
      });
    }
  }, [value, languages]);

  const handleInputChange = (languageId: number, inputValue: string) => {
    const updatedValues = localValues.map(item =>
      item.languageId === languageId
        ? { ...item, value: inputValue }
        : item
    );
    
    setLocalValues(updatedValues);
    
    // Only call onChange if provided
    if (onChange) {
      onChange(updatedValues);
    }
  };

  const getLanguageName = (language: ILanguage): string => {
    switch (language.locale) {
      case 'sv':
        return 'Svenska';
      case 'da':
        return 'Danska';
      case 'no':
        return 'Norska';
      default:
        return language.locale.toUpperCase();
    }
  };

  const getCurrentValue = (languageId: number): string => {
    const currentItem = localValues.find(item => item.languageId === languageId);
    return currentItem?.value || '';
  };

  const tabItems = languages.map(language => ({
    key: language.id.toString(),
    label: getLanguageName(language),
    children: (
      <div>
        {type === 'textarea' ? (
          <Input.TextArea
            value={getCurrentValue(language.id)}
            onChange={(e) => handleInputChange(language.id, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            status={status}
            id={id ? `${id}-${language.id}` : undefined}
          />
        ) : (
          <Input
            value={getCurrentValue(language.id)}
            onChange={(e) => handleInputChange(language.id, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            status={status}
            id={id ? `${id}-${language.id}` : undefined}
          />
        )}
      </div>
    )
  }));

  if (languages.length === 0) {
    return (
      <div style={{ padding: '8px', color: '#999' }}>
        Laddar språk...
      </div>
    );
  }

  return (
    <div>
      {label && (
        <div style={{ marginBottom: '8px', fontWeight: 500 }}>
          {label}
          {required && <span style={{ color: '#ff4d4f' }}> *</span>}
        </div>
      )}
      <StyledTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="small"
        type="card"
      />
    </div>
  );
};

export default LocalisedFieldInput;
